import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useGetCompanySizesQuery,
  useUpdateClientMutation,
  useUpdateCompanyMutation,
  useUpdateSubscriptionMutation,
} from '@bvi/admin-panel/entities/clients/api-slice';
import { selectCompanySizes } from '@bvi/admin-panel/entities/clients/selectors';
import { IEditCompanyFormProperties } from '@bvi/admin-panel/feature/clients/ui/edit-company-form/types';
import { formatCompanySizeLabel } from '@bvi/api-interfaces/company-size';
import {
  AccountStatus,
  PaymentMethod,
} from '@bvi/api-interfaces/entity/client';
import { isServerError } from '@bvi/axios-query';
import { Form, FormDatePicker } from '@bvi/common-components';
import { NotistackMessageVariants, useNotistack } from '@bvi/notistack';

import { buildValidationSchema } from './schema';

interface IEditClientFormData {
  name: string;
  contactPerson: string;
  contactEmail: string;
  companySizeId: number;
  emailDomain: string;
  signatoryName?: string | null;
  signatoryEmail?: string | null;
  billingContactName?: string | null;
  billingContactEmail?: string | null;
  accountManagerName?: string | null;
  accountManagerEmail?: string | null;
  supportLiaisonName?: string | null;
  supportLiaisonEmail?: string | null;
  subscriptionStartsAt: number | Date;
  subscriptionExpiresAt: number | Date;
  subscriptionTermMonths: number;
  orientationAt?: number | Date | null;
  paymentMethod?: PaymentMethod | null;
  paymentMethodNotes?: string | null;
  accountStatus: AccountStatus;
  pricingNarrative?: string | null;
}

const ACCOUNT_STATUSES = [AccountStatus.Inactive, AccountStatus.Active];

const PAYMENT_METHODS = [
  PaymentMethod.MonthlyAch,
  PaymentMethod.UpfrontPrepay,
  PaymentMethod.WireTransfer,
  PaymentMethod.CreditCard,
  PaymentMethod.Other,
];

export const EditCompanyForm: FC<IEditCompanyFormProperties> = ({
  client,
  onSuccess,
}) => {
  const {
    id: clientId,
    company,
    subscription,
    accountManagerName,
    accountManagerEmail,
    supportLiaisonName,
    supportLiaisonEmail,
    orientationAt,
    paymentMethod: initialPaymentMethod,
    paymentMethodNotes: initialPaymentMethodNotes,
    accountStatus,
    pricingNarrative,
  } = client;
  const { data: companySizes, isLoading: isCompanySizesLoading } =
    useGetCompanySizesQuery(undefined, {
      selectFromResult: ({ data, isLoading }) => ({
        data: selectCompanySizes(data),
        isLoading,
      }),
    });

  const [updateCompanyRequest, { isLoading: isUpdatingCompany, error }] =
    useUpdateCompanyMutation();
  const [
    updateClientRequest,
    { isLoading: isUpdatingClient, error: clientError },
  ] = useUpdateClientMutation();
  const [
    updateSubscriptionRequest,
    { isLoading: isUpdatingSubscription, error: subscriptionError },
  ] = useUpdateSubscriptionMutation();
  const { t } = useTranslation();
  const { showNotificationMessage } = useNotistack();

  const validationSchema = buildValidationSchema(t);
  const methods = useForm<IEditClientFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: company.name,
      contactPerson: company.contactPerson,
      contactEmail: company.contactEmail,
      emailDomain: company.emailDomain ?? '',
      companySizeId: company.size.id,
      signatoryName: company.signatoryName ?? '',
      signatoryEmail: company.signatoryEmail ?? '',
      billingContactName: company.billingContactName ?? '',
      billingContactEmail: company.billingContactEmail ?? '',
      accountManagerName: accountManagerName ?? '',
      accountManagerEmail: accountManagerEmail ?? '',
      supportLiaisonName: supportLiaisonName ?? '',
      supportLiaisonEmail: supportLiaisonEmail ?? '',
      subscriptionStartsAt: subscription.startsAt,
      subscriptionExpiresAt: subscription.expiresAt,
      subscriptionTermMonths: subscription.termMonths ?? 12,
      orientationAt: orientationAt ?? null,
      paymentMethod: initialPaymentMethod ?? undefined,
      paymentMethodNotes: initialPaymentMethodNotes ?? '',
      accountStatus,
      pricingNarrative: pricingNarrative ?? '',
    },
  });
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const subscriptionStartsAt = watch('subscriptionStartsAt');
  const subscriptionTermMonths = watch('subscriptionTermMonths');
  const paymentMethod = watch('paymentMethod');

  useEffect(() => {
    if (subscriptionStartsAt == null || !subscriptionTermMonths) {
      return;
    }

    setValue(
      'subscriptionExpiresAt',
      dayjs(subscriptionStartsAt)
        .add(Number(subscriptionTermMonths), 'month')
        .toDate(),
      { shouldValidate: false },
    );
  }, [subscriptionStartsAt, subscriptionTermMonths, setValue]);

  const onSubmit = async (data: IEditClientFormData) => {
    try {
      await Promise.all([
        updateCompanyRequest([
          company.id,
          {
            name: data.name,
            contactPerson: data.contactPerson,
            contactEmail: data.contactEmail,
            emailDomain: data.emailDomain,
            companySizeId: data.companySizeId,
            signatoryName: data.signatoryName?.trim()
              ? data.signatoryName
              : null,
            signatoryEmail: data.signatoryEmail?.trim()
              ? data.signatoryEmail
              : null,
            billingContactName: data.billingContactName?.trim()
              ? data.billingContactName
              : null,
            billingContactEmail: data.billingContactEmail?.trim()
              ? data.billingContactEmail
              : null,
          },
        ]).unwrap(),
        updateClientRequest([
          clientId,
          {
            accountStatus: data.accountStatus,
            paymentMethod: data.paymentMethod ?? null,
            paymentMethodNotes:
              data.paymentMethod === PaymentMethod.Other &&
              data.paymentMethodNotes?.trim()
                ? data.paymentMethodNotes.trim()
                : null,
            accountManagerName: data.accountManagerName?.trim()
              ? data.accountManagerName
              : null,
            accountManagerEmail: data.accountManagerEmail?.trim()
              ? data.accountManagerEmail
              : null,
            supportLiaisonName: data.supportLiaisonName?.trim()
              ? data.supportLiaisonName
              : null,
            supportLiaisonEmail: data.supportLiaisonEmail?.trim()
              ? data.supportLiaisonEmail
              : null,
            pricingNarrative: data.pricingNarrative ?? null,
            orientationAt: data.orientationAt
              ? new Date(data.orientationAt).getTime()
              : null,
          },
        ]).unwrap(),
        updateSubscriptionRequest([
          subscription.id,
          {
            subscriptionStartsAt: new Date(data.subscriptionStartsAt).getTime(),
            subscriptionExpiresAt: new Date(
              data.subscriptionExpiresAt,
            ).getTime(),
            subscriptionTermMonths: data.subscriptionTermMonths,
          },
        ]).unwrap(),
      ]);
      onSuccess?.();
    } catch {
      // Error toast is handled via the mutation error effect below.
    }
  };

  useEffect(() => {
    const nextError = error ?? clientError ?? subscriptionError;
    if (isServerError(nextError)) {
      showNotificationMessage(
        { type: nextError.type },
        NotistackMessageVariants.ERROR,
      );
    }
  }, [error, clientError, subscriptionError, showNotificationMessage]);

  const isLoading =
    isUpdatingCompany || isUpdatingClient || isUpdatingSubscription;

  return (
    <FormProvider {...methods}>
      <Typography variant="h1" mb={2}>
        {t('companies.edit.title')}
      </Typography>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} tabIndex={1}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('clients.add.subtitleCompany')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('name')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.name.label')}
              placeholder={t('clients.add.form.name.placeholder')}
              error={Boolean(errors.name)}
              helperText={<>{errors.name?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-company-size"
                error={Boolean(errors.companySizeId)}
              >
                {t('clients.add.form.companySize.label')}
              </InputLabel>
              <Controller
                name="companySizeId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(Number(event.target.value))
                    }
                    label={t('clients.add.form.companySize.label')}
                    labelId="select-company-size"
                    placeholder={t('clients.add.form.companySize.placeholder')}
                    error={Boolean(errors.companySizeId)}
                    disabled={isCompanySizesLoading}
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 420 } },
                    }}
                  >
                    {companySizes.map((companySize) => (
                      <MenuItem key={companySize.id} value={companySize.id}>
                        {formatCompanySizeLabel(companySize.name)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.companySizeId && (
                <FormHelperText error>
                  {errors.companySizeId?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register('emailDomain')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.emailDomain.label')}
              placeholder={t('clients.add.form.emailDomain.placeholder')}
              error={Boolean(errors.emailDomain)}
              helperText={
                errors.emailDomain?.message ??
                t('clients.add.form.emailDomain.helper')
              }
              FormHelperTextProps={{
                sx: { whiteSpace: 'normal', fontWeight: 400 },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('clients.add.subtitleContacts')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('contactPerson')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.contactPerson.label')}
              placeholder={t('clients.add.form.contactPerson.placeholder')}
              error={Boolean(errors.contactPerson)}
              helperText={<>{errors.contactPerson?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('contactEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.contactEmail.label')}
              placeholder={t('clients.add.form.contactEmail.placeholder')}
              error={Boolean(errors.contactEmail)}
              helperText={<>{errors.contactEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('signatoryName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.signatoryName.label')}
              placeholder={t('clients.add.form.signatoryName.placeholder')}
              error={Boolean(errors.signatoryName)}
              helperText={<>{errors.signatoryName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('signatoryEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.signatoryEmail.label')}
              placeholder={t('clients.add.form.signatoryEmail.placeholder')}
              error={Boolean(errors.signatoryEmail)}
              helperText={<>{errors.signatoryEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('billingContactName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.billingContactName.label')}
              placeholder={t('clients.add.form.billingContactName.placeholder')}
              error={Boolean(errors.billingContactName)}
              helperText={<>{errors.billingContactName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('billingContactEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.billingContactEmail.label')}
              placeholder={t(
                'clients.add.form.billingContactEmail.placeholder',
              )}
              error={Boolean(errors.billingContactEmail)}
              helperText={<>{errors.billingContactEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('accountManagerName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.accountManagerName.label')}
              placeholder={t('clients.add.form.accountManagerName.placeholder')}
              error={Boolean(errors.accountManagerName)}
              helperText={<>{errors.accountManagerName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('accountManagerEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.accountManagerEmail.label')}
              placeholder={t(
                'clients.add.form.accountManagerEmail.placeholder',
              )}
              error={Boolean(errors.accountManagerEmail)}
              helperText={<>{errors.accountManagerEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('supportLiaisonName')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.supportLiaisonName.label')}
              placeholder={t('clients.add.form.supportLiaisonName.placeholder')}
              error={Boolean(errors.supportLiaisonName)}
              helperText={<>{errors.supportLiaisonName?.message}</>}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              {...register('supportLiaisonEmail')}
              variant="outlined"
              fullWidth
              label={t('clients.add.form.supportLiaisonEmail.label')}
              placeholder={t(
                'clients.add.form.supportLiaisonEmail.placeholder',
              )}
              error={Boolean(errors.supportLiaisonEmail)}
              helperText={<>{errors.supportLiaisonEmail?.message}</>}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('clients.add.subtitleSubscription')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Stack spacing={2}>
              <FormDatePicker
                valueFormat="jsDate"
                field="subscriptionStartsAt"
                label={t('clients.add.form.subscriptionStartsAt.label')}
                error={Boolean(errors.subscriptionStartsAt)}
                helperText={<>{errors.subscriptionStartsAt?.message}</>}
              />
              <FormDatePicker
                valueFormat="jsDate"
                field="subscriptionExpiresAt"
                label={t('clients.add.form.subscriptionExpiresAt.label')}
                error={Boolean(errors.subscriptionExpiresAt)}
                helperText={<>{errors.subscriptionExpiresAt?.message}</>}
              />
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack spacing={2}>
              <TextField
                {...register('subscriptionTermMonths', { valueAsNumber: true })}
                variant="outlined"
                fullWidth
                type="number"
                inputProps={{ min: 1, step: 1 }}
                label={t('clients.add.form.subscriptionTermMonths.label')}
                placeholder={t(
                  'clients.add.form.subscriptionTermMonths.placeholder',
                )}
                error={Boolean(errors.subscriptionTermMonths)}
                helperText={<>{errors.subscriptionTermMonths?.message}</>}
              />
              <FormDatePicker
                valueFormat="jsDate"
                field="orientationAt"
                label={t('clients.add.form.orientationAt.label')}
                error={Boolean(errors.orientationAt)}
                helperText={<>{errors.orientationAt?.message}</>}
                clearable
              />
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-payment-method"
                error={Boolean(errors.paymentMethod)}
              >
                {t('clients.add.form.paymentMethod.label')}
              </InputLabel>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    label={t('clients.add.form.paymentMethod.label')}
                    labelId="select-payment-method"
                    error={Boolean(errors.paymentMethod)}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {t(`clients.paymentMethods.${method}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.paymentMethod && (
                <FormHelperText error>
                  {errors.paymentMethod?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                id="select-account-status"
                error={Boolean(errors.accountStatus)}
              >
                {t('clients.add.form.accountStatus.label')}
              </InputLabel>
              <Controller
                name="accountStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? AccountStatus.Inactive}
                    label={t('clients.add.form.accountStatus.label')}
                    labelId="select-account-status"
                    error={Boolean(errors.accountStatus)}
                  >
                    {ACCOUNT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {t(`clients.accountStatuses.${status}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.accountStatus && (
                <FormHelperText error>
                  {errors.accountStatus?.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          {paymentMethod === PaymentMethod.Other && (
            <Grid item xs={12}>
              <TextField
                {...register('paymentMethodNotes')}
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                label={t('clients.add.form.paymentMethodNotes.label')}
                placeholder={t(
                  'clients.add.form.paymentMethodNotes.placeholder',
                )}
                error={Boolean(errors.paymentMethodNotes)}
                helperText={<>{errors.paymentMethodNotes?.message}</>}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              {...register('pricingNarrative')}
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              label={t('clients.add.form.pricingNarrative.label')}
              placeholder={t('clients.add.form.pricingNarrative.placeholder')}
              error={Boolean(errors.pricingNarrative)}
              helperText={<>{errors.pricingNarrative?.message}</>}
            />
          </Grid>
          <Grid item xs={12} marginTop={1}>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isLoading}
              autoFocus
            >
              {t('companies.edit.form.submit')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Form>
    </FormProvider>
  );
};
